'use client';

import SettingConfig from '@module/config/setting';
import { itemsPerPage } from '@module/constants/pagenation';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import { useFanRankCancelMutation, useFanRankListQuery } from '@module/queries/fan/fanQueryHooks';
import { useBjInfoQuery } from '@module/queries/member/memberQueryHooks';
import useModalStore from '@module/stores/useModalStore';
import { MODAL_TYPE_PC } from '@module/types/modal/type';
import arrayToIdxObject from '@module/utils/arrayToIdxObject';
import catchAllErrors from '@module/utils/catchAllErrors';
import getFirstDayOfPreviousMonths from '@module/utils/date/getFirstDayOfCurrentMonth';
import { BaseHttpResponseError } from '@module/utils/error/apiErrors';
import formatNumberWithComma from '@module/utils/formatNumberWithComma';
import masking from '@module/utils/masking';
import BaseButton from '@pc/components/atoms/button/BaseButton';
import BasePagenation from '@pc/components/pagenation/BasePagenation';
import NotFoundDisplay from '@pc/components/result/NotFoundDisplay';
import TableItemsTotalNumber from '@pc/components/table/TableItemsTotalNumber';
import { Checkbox } from '@pc/components/ui/checkbox';
import { Input } from '@pc/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@pc/components/ui/table';
import type { SendMessageModalType } from '@pc/containers/modal/message/type';
import { keepPreviousData } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import type React from 'react';
import { useEffect, useState } from 'react';

function AllRankingContainer() {
  const params = useParams<{ id: string }>();
  const channelId = params?.id ? decodeURIComponent(params.id) : '';
  const { isLogin, userInfo, isPostAvailable } = useGetLoginInfoQuery();
  const isMyChannel = channelId === userInfo?.id;

  const { pushModal, popModal, clearModals } = useModalStore();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const currentPageFirstOffset = (currentPage - 1) * itemsPerPage;

  const [searchValue, setSearchValue] = useState<string>('');
  const [searchValueForQuery, setSearchValueForQuery] = useState<string>('');
  const [checkedIdxList, setCheckedIdxList] = useState<number[]>([]);

  const { data: bjInfoData } = useBjInfoQuery({
    userId: channelId,
    info: 'media fanGrade adminBjNotice exchange checkEnter bookmark',
  });

  const { data: rankList, refetch: refetchRankList } = useFanRankListQuery(
    {
      offset: currentPageFirstOffset,
      limit: itemsPerPage,
      noCache: true,
      userIdx: bjInfoData?.bjInfo.idx,
      type: 'all',
      fromUserSearch: searchValueForQuery,
      date: getFirstDayOfPreviousMonths(0),
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    setCheckedIdxList([]);
  }, [rankList]);

  const showStoreModal = ({ giftId, giftNick }: { giftId: string; giftNick: string }) => {
    if (userInfo?.authYN === 'N') {
      pushModal({
        type: 'IDENTITY_VERIFICATION_MODAL',
      });
      return;
    }
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        type: MODAL_TYPE_PC.AUTH_MODAL,
        id: 'AllRankingContainer - 1',
      });
      return;
    }
    pushModal({
      type: MODAL_TYPE_PC.STORE_MODAL,
      id: 'AllRankingContainer - 2',
      options: {
        giftId,
        giftNick,
      },
    });
  };

  const showSendMessageModal = ({ userIdx, userId, userNick }: SendMessageModalType) => {
    if (userInfo?.authYN === 'N') {
      pushModal({
        type: 'IDENTITY_VERIFICATION_MODAL',
      });
      return;
    }
    if (!isLogin) {
      if (aspCond.if('new-asp')) {
        SettingConfig.aspUtils?.goLogin();
        return;
      }
      pushModal({
        type: MODAL_TYPE_PC.AUTH_MODAL,
        id: 'AllRankingContainer - 3',
      });
      return;
    }
    pushModal({
      type: MODAL_TYPE_PC.SEND_MESSAGE_MODAL,
      id: 'AllRankingContainer - 4',
      options: {
        userIdx,
        userId,
        userNick,
      },
    });
  };

  const { mutate: cancelFanRank } = useFanRankCancelMutation({
    onSuccess: (data) => {
      refetchRankList();
      setCheckedIdxList([]);
      pushModal({
        type: MODAL_TYPE_PC.CONFIRM_MODAL,
        id: 'AllRankingContainer - 5',
        options: {
          description: data.message,
          onClose: clearModals,
        },
      });
    },
    onError: (err) => {
      if (err instanceof BaseHttpResponseError) {
        catchAllErrors(err.message, () => {
          pushModal({
            type: MODAL_TYPE_PC.CONFIRM_MODAL,
            id: 'AllRankingContainer - 6',
            options: {
              description: err.message,
              onClose: popModal,
            },
          });
        });
      }
    },
  });

  const handleSearchClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchValue.length === 1) {
      pushModal({
        type: 'CONFIRM_MODAL',
        options: { description: '아이디 · 닉네임은 2자 이상 입력해 주세요' },
      });
      return;
    }
    setSearchValueForQuery(searchValue);
    setCurrentPage(1);
  };

  const handleSelectAllClick = (isChecked: boolean) => {
    if (!rankList) return;
    if (isChecked) {
      const allIdxList = rankList.list.map((i) => i.userIdx);
      setCheckedIdxList(allIdxList);
    } else {
      setCheckedIdxList([]);
    }
  };

  const handleCancelButtonClick = () => {
    if (checkedIdxList.length < 1) {
      pushModal({
        type: MODAL_TYPE_PC.CONFIRM_MODAL,
        id: 'AllRankingContainer - 7',
        options: {
          description: '팬을 선택해 주세요.',
        },
      });
      return;
    }
    if (!rankList) return;

    const firstSelectedUserInfo = rankList.list.find((i) => i.userIdx === checkedIdxList[0]);
    if (!firstSelectedUserInfo) return;
    pushModal({
      type: MODAL_TYPE_PC.CONFIRM_CANCEL_MODAL,
      id: 'AllRankingContainer - 8',
      options: {
        description: `${masking(firstSelectedUserInfo.userId)}(${firstSelectedUserInfo.userNick}) 님${checkedIdxList.length === 1 ? '' : `외 ${checkedIdxList.length - 1}명`}을 영구팬에서 해제하시겠습니까?`,
        onClick: () => {
          cancelFanRank(arrayToIdxObject(checkedIdxList, 'toUserIdx'));
          popModal();
        },
        rightText: '해제',
      },
    });
  };

  const handleWriteButtonClick = () => {
    if (checkedIdxList.length < 1) {
      pushModal({
        type: MODAL_TYPE_PC.CONFIRM_MODAL,
        id: 'AllRankingContainer - 9',
        options: {
          description: '팬을 선택해 주세요.',
        },
      });
      return;
    }
    if (!rankList) return;
    const firstSelectedUserInfo = rankList.list.find((i) => i.userIdx === checkedIdxList[0]);
    if (!firstSelectedUserInfo) return;
    pushModal({
      type: MODAL_TYPE_PC.FAN_RANK_WRITE_MODAL,
      id: 'AllRankingContainer - 10',
      options: {
        title: `${firstSelectedUserInfo.userNick}(${masking(firstSelectedUserInfo.userId)})님${checkedIdxList.length === 1 ? '' : `외 ${checkedIdxList.length - 1}명`} 영구팬 설정`,
        targetIdxList: checkedIdxList,
        channelId,
        onWrite: () => {
          refetchRankList();
        },
        nick: firstSelectedUserInfo.userNick,
        userId: firstSelectedUserInfo.userId,
      },
    });
  };

  const isListEmpty = !rankList?.page.total;
  const [allCehck, setAllCheck] = useState(false);

  return (
    <div className='mb-20'>
      {isMyChannel && (
        <>
          <form className='flex justify-end' onSubmit={handleSearchClick}>
            <Input
              type='text'
              placeholder={'닉네임·아이디'}
              value={searchValue}
              containerClassName='w-40 border'
              onChange={(e) => {
                setSearchValue(e.currentTarget.value);
              }}
            />
            <BaseButton type='submit' variant='primary' size='md' className='ml-2 min-w-[68px] text-[17px]'>
              {'검색'}
            </BaseButton>
          </form>
          <TableItemsTotalNumber className='mb-4' count={rankList?.page.total ?? 0} />
        </>
      )}

      <Table>
        <colgroup>
          {isMyChannel && <col width='4%' />}
          <col width='4%' />
          <col width='4%' />
          <col />
          {isMyChannel && <col />}
          {isMyChannel && <col width='6%' />}
          {!aspCond.if('asp') && <col width='6%' />}
          {isPostAvailable && <col width='6%' />}
        </colgroup>
        <TableHeader>
          <TableRow className='h-[50px]'>
            {isMyChannel && (
              <TableHead>
                <Checkbox
                  checked={allCehck && checkedIdxList.length === rankList?.list.length}
                  onCheckedChange={(checked) => {
                    if (typeof checked !== 'boolean') return;
                    setAllCheck((prev) => !prev);
                    handleSelectAllClick(checked);
                  }}
                />
              </TableHead>
            )}
            <TableHead>{'순위'}</TableHead>
            <TableHead>{'등급'}</TableHead>
            <TableHead>{'닉네임·아이디'}</TableHead>
            {isMyChannel && (
              <>
                <TableHead>{'하트수'}</TableHead>
                <TableHead>{'영구여부'}</TableHead>
              </>
            )}
            {!aspCond.if('asp') && <TableHead>{'선물'}</TableHead>}
            {isPostAvailable && <TableHead>{'쪽지'}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rankList?.list.map((row) => (
            <TableRow key={row.userIdx} className='text-colorFg18'>
              {isMyChannel && (
                <TableCell>
                  <Checkbox
                    checked={checkedIdxList.includes(row.userIdx)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setCheckedIdxList((prev) => [...prev, row.userIdx]);
                      } else {
                        setCheckedIdxList((prev) => prev.filter((i) => i !== row.userIdx));
                      }
                    }}
                  />
                </TableCell>
              )}
              <TableCell>{row.rank}</TableCell>
              <TableCell>
                <div className='flex w-full justify-center'>
                  <img
                    src={`${SettingConfig.S3_BUCKET_URL}/img/grade/grade_${row.fanLevel}.svg`}
                    alt='grade'
                    className='h-[23px]'
                  />
                </div>
              </TableCell>
              <TableCell className='text-left'>
                <div className='flex items-center'>
                  <span>{`${row.userNick} · ${masking(row.userId)}`}</span>
                  {row.rank === 1 && (
                    <img
                      src={`${SettingConfig.S3_BUCKET_URL}/img/grade/fan_king.png`}
                      alt='king'
                      className='ml-2 h-[23px]'
                    />
                  )}
                </div>
              </TableCell>
              {isMyChannel && (
                <>
                  <TableCell>{`${formatNumberWithComma(row.totalCoin)}개`}</TableCell>
                  <TableCell className='text-center'>{row.isEverFan ? '영구' : ''}</TableCell>
                </>
              )}
              {!aspCond.if('asp') && (
                <TableCell className='text-center'>
                  <button type='button' onClick={() => showStoreModal({ giftId: row.userId, giftNick: row.userNick })}>
                    <img
                      alt='gift'
                      src={`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_gift3.png`}
                      width={22}
                      height={22}
                    />
                  </button>
                </TableCell>
              )}
              {isPostAvailable && (
                <TableCell className='text-center'>
                  <button
                    type='button'
                    onClick={() =>
                      showSendMessageModal({
                        userIdx: row.userIdx,
                        userId: row.userId,
                        userNick: row.userNick,
                      })
                    }
                  >
                    <img alt='post' src={`${SettingConfig.S3_BUCKET_URL}/img/icon/letter.png`} width={20} height={20} />
                  </button>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {isListEmpty && <NotFoundDisplay text='리스트가 없습니다.' />}

      {isMyChannel && (
        <BasePagenation currentPage={currentPage} setCurrentPage={setCurrentPage} pageData={rankList?.page} />
      )}

      {isMyChannel && (
        <div className='mt-20 flex justify-center gap-2'>
          <BaseButton size='md' className='h-12 px-5' onClick={handleCancelButtonClick}>
            {'영구해제'}
          </BaseButton>
          <BaseButton size='md' className='h-12 px-5' onClick={handleWriteButtonClick}>
            {'영구지정'}
          </BaseButton>
        </div>
      )}
    </div>
  );
}

export default AllRankingContainer;
